import { subject } from "@casl/ability";
import { Can } from "@dashboard/casl/taskAbility";
import { TaskStatus, TaskStatusSelect } from "@dashboard/components/TaskStatusSelect";
import type { TaskWithOccurrence } from "@dashboard/utils/taskWithOccurrence";
import { formatDateShort } from "@dashboard/utils/formatDate";
import { useTranslation } from "react-i18next";
import { faLink } from "@fortawesome/pro-regular-svg-icons";
import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { cn } from "frontend-components";

type AnnualCycleCardProps = {
    task: TaskWithOccurrence;
    onOpen: (taskId: number) => void;
};

export default function AnnualCycleCard({ task, onOpen }: AnnualCycleCardProps) {
    const { i18n } = useTranslation();
    const occurrence = task.occurrence;
    const links = task.links ?? [];
    const dueDate = formatDateShort(task.dueDate, i18n.language);

    return (
        <li
            className={cn(
                "rounded-[3px] border border-compendia-border bg-compendia-card px-3 py-3 transition-colors hover:bg-compendia-greige-20 sm:px-4",
                task.isExpired && "border-l-4 border-l-chameleon-pink",
            )}
        >
            <div className="flex items-start justify-between gap-3">
                <button
                    type="button"
                    onClick={() => onOpen(task.id)}
                    className="min-w-0 flex-1 cursor-pointer text-left text-base font-medium text-compendia-foreground line-clamp-2 wrap-break-word hover:underline"
                >
                    {task.title}
                </button>
                <div className="shrink-0">
                    {occurrence ? (
                        <Can I="update" this={subject("TaskOccurrence", { task })} passThrough>
                            {(allowed) =>
                                allowed ? (
                                    <TaskStatusSelect
                                        taskOccurrenceId={occurrence.id}
                                        currentStatus={occurrence.status}
                                    />
                                ) : (
                                    <TaskStatus status={occurrence.status} />
                                )
                            }
                        </Can>
                    ) : (
                        <TaskStatus status="OPEN" />
                    )}
                </div>
            </div>

            <div className="mt-3 flex items-center justify-between gap-3">
                <div className="min-w-0 truncate text-label text-compendia-foreground">
                    {task.ownerUser
                        ? `${task.ownerUser.firstName} ${task.ownerUser.lastName}`
                        : null}
                </div>
                <div className="flex shrink-0 items-center gap-3">
                    {task.isExpired ? (
                        <span className="flex items-center gap-1 text-label font-medium text-chameleon-pink">
                            {dueDate}
                            <FontAwesomeIcon icon={faTriangleExclamation} />
                        </span>
                    ) : (
                        <span className="text-label text-compendia-muted">{dueDate}</span>
                    )}
                </div>
            </div>

            {links.length > 0 && (
                <div className="mt-2 flex items-center gap-1">
                    <FontAwesomeIcon icon={faLink} className="text-compendia-muted" />
                    <a
                        href={links[0].url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="truncate text-sm text-text-secondary hover:underline"
                    >
                        {links[0].title}
                    </a>
                    {links.length > 1 && (
                        <span className="shrink-0 rounded-full bg-compendia-greige px-1.5 py-0.5 text-xs text-compendia-foreground">
                            +{links.length - 1}
                        </span>
                    )}
                </div>
            )}
        </li>
    );
}
